// Quality API: the NCR claim workflows (collection, credit, replacement).
// Split out of quality.ts (500-line rule) and spread into qualityAPI, so
// callers keep using qualityAPI.initiateCollection(...) and the rest.
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  NCRCollectionProcess,
  MyCollectionQueueRow,
  MyCollectionsCount,
  CollectionAssignee,
  NCRCreditProcess,
  NCRReplacementProcess,
} from './types';

export const qualityClaimsAPI = {
  // Collection Process - Multi-step workflow
  getCollectionProcess: (ncrId: number): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.get(`/quality/ncrs/${ncrId}/collection/`),

  initiateCollection: (ncrId: number, data: {
    proposed_collection_date?: string;
    collection_address?: string;
    collection_notes?: string;
    customer_notes?: string;
    /** CR-7352: optional customer-side owner, chosen on the Initiate form. */
    assigned_to?: number | null;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/initiate/`, data),

  /**
   * CR-7352: (re)assign a collection, or pass null to clear the owner.
   * Customer-side only; the API rejects a completed collection.
   */
  assignCollection: (ncrId: number, assignedTo: number | null): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/assign/`, { assigned_to: assignedTo }),

  /**
   * CR-7352: the names this NCR's collection may be assigned to — active
   * users in the NCR's own company. Customer-side only; a supplier gets 403.
   */
  getCollectionAssignees: (ncrId: number): Promise<AxiosResponse<CollectionAssignee[]>> =>
    api.get(`/quality/ncrs/${ncrId}/collection/assignees/`),

  /** CR-7352: open collections assigned to the signed-in user. */
  getMyCollections: (params?: Record<string, string | number | undefined>): Promise<AxiosResponse<PaginatedResponse<MyCollectionQueueRow>>> =>
    api.get('/quality/ncrs/my-collections/', { params }),

  /** CR-7352: how many are waiting on me — drives the menu badge. */
  /** CR-5889: `covering` is how many of them belong to someone the user covers for. */
  getMyCollectionsCount: (): Promise<AxiosResponse<MyCollectionsCount>> =>
    api.get('/quality/ncrs/my-collections/count/'),

  /** CR-7352: mark an assigned collection completed without opening the NCR. */
  completeCollection: (ncrId: number, data?: {
    actual_collection_date?: string;
    customer_notes?: string;
    supplier_receipt_notes?: string;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/complete/`, data ?? {}),

  updateCollectionStatus: (ncrId: number, data: {
    status: string;
    collection_notes?: string;
    confirmed_collection_date?: string;
    tracking_reference?: string;
    actual_collection_date?: string;
    supplier_receipt_notes?: string;
    /** CR-7352: sent from Master Edit so an admin can complete before the supplier confirms. */
    master_edit?: boolean;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/update-status/`, data),

  // Credit Process - Multi-step workflow
  getCreditProcess: (ncrId: number): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.get(`/quality/ncrs/${ncrId}/credit/`),

  initiateCredit: (ncrId: number, data: {
    credit_amount?: number;
    credit_currency?: string;
    credit_reason?: string;
    customer_notes?: string;
  }): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/initiate/`, data),

  // Supplier submits/re-submits the credit note (file + number) — multipart.
  submitCreditNote: (ncrId: number, data: FormData): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/submit-note/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Customer approves or rejects the submitted credit note.
  updateCreditStatus: (ncrId: number, data: {
    status: string;
    credit_amount_confirmed?: number;
    customer_credit_reference?: string;
    approval_notes?: string;
  }): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/update-status/`, data),

  // Replacement Process - Multi-step workflow (mirrors Credit)
  initiateReplacement: (ncrId: number, data: {
    replacement_qty?: number;
    replacement_reason?: string;
    customer_notes?: string;
  }): Promise<AxiosResponse<NCRReplacementProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/replacement/initiate/`, data),

  // Supplier submits/re-submits the despatch details (reference + optional doc) — multipart.
  submitReplacementDispatch: (ncrId: number, data: FormData): Promise<AxiosResponse<NCRReplacementProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/replacement/submit-dispatch/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Customer confirms receipt of, or rejects, the despatched replacement.
  updateReplacementStatus: (ncrId: number, data: {
    status: string;
    customer_replacement_reference?: string;
    approval_notes?: string;
  }): Promise<AxiosResponse<NCRReplacementProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/replacement/update-status/`, data),
};
